import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Star, MapPin, Clock } from "lucide-react";

const SLIDES = [
  {
    bg: "/images/gallery/2.webp",
    bgFallback: "/images/gallery/2.png",
    tag: "Nepalese & Indian Kitchen · Gainesville, FL",
    title: "Welcome to",
    highlight: "himalayan Bowl & Juice Bar",
    sub: "Steaming Momo, Chowmin, Samosa & Fresh-pressed juices — all under one roof in Gainesville",
  },
  {
    bg: "/images/hero-1.webp",
    bgFallback: "/images/hero-1.png",
    tag: "Inspired by the Roof of the World",
    title: "A Taste of the",
    highlight: "Himalayas",
    sub: "Flavors born in the shadow of Everest — brought fresh to your table every day in Gainesville, FL",
  },
  {
    bg: "/images/hero-2.webp",
    bgFallback: "/images/hero-2.png",
    tag: "Handcrafted · Momo · Chowmin · Thukpa",
    title: "Authentic Flavors",
    highlight: "from Nepal",
    sub: "Our chefs bring real Kathmandu kitchen traditions to Gainesville — every dish made from scratch, every day",
  },
  {
    bg: "/images/hero-3.webp",
    bgFallback: "/images/hero-3.png",
    tag: "Generations of Himalayan Tradition",
    title: "Spiced with",
    highlight: "Heritage & Love",
    sub: "Recipes passed through Nepali and Indian families for generations — now yours to discover right here in Gainesville",
  },
  {
    bg: "/images/hero-4.webp",
    bgFallback: "/images/hero-4.png",
    tag: "Samosa · Jalebi · Pakoda · Fresh Juice",
    title: "Made from Scratch",
    highlight: "Every Day",
    sub: "From hearty Momo to fragrant biryani and cold-pressed Himalayan juice — bold flavors, fresh ingredients",
  },
];

function scrollTo(href) {
  document
    .querySelector(href)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  const [idx, setIdx] = useState(0);

  const next = useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);

  useEffect(() => {
    const t = setTimeout(next, 6000);
    return () => clearTimeout(t);
  }, [idx, next]);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* ── Slide backgrounds ── */}
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeInOut" }}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat animate-ken-burns"
          style={{ backgroundImage: `url(${SLIDES[idx].bg}), url(${SLIDES[idx].bgFallback})` }}
          aria-hidden="true"
        />
      </AnimatePresence>

      {/* Dark vignette — heavier at top & bottom, lighter in middle to show landscape */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,34,96,0.78) 0%, rgba(15,34,96,0.35) 45%, rgba(26,18,0,0.55) 75%, rgba(15,34,96,0.82) 100%)",
        }}
      />
      {/* Bottom fade to cream */}
      <div
        className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-cream to-transparent"
        aria-hidden="true"
      />

      {/* ── Content ── */}
      <div className="relative z-10 text-center px-5 max-w-4xl mx-auto pt-28 pb-32">
        {/* Visually hidden h1 for SEO — always present for crawlers */}
        <h1 className="sr-only">
          Himalayan Bowl &amp; Juice Bar — Authentic Nepalese &amp; Indian
          Restaurant in Gainesville, FL. Hand-folded Momo, Thukpa, Cold-Pressed
          Juices &amp; More.
        </h1>
        {/* Floating logo above headline */}
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.85 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center mb-6"
        >
          <picture>
            <source srcSet="/logo.webp" type="image/webp" />
            <img
              src="/logo.png"
              alt="Himalayan Bowl & Juice Bar logo"
              className="h-28 sm:h-36 md:h-44 drop-shadow-2xl animate-float"
              style={{ filter: "drop-shadow(0 8px 24px rgba(245,166,35,0.45))" }}
              fetchPriority="high"
            />
          </picture>
        </motion.div>

        {/* Slide tag badge */}
        <motion.div
          key={`tag-${idx}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="inline-flex items-center gap-2 bg-brand-orange/15 border border-brand-orange/40 text-brand-orange-light
            px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.18em] uppercase mb-6 backdrop-blur-sm"
        >
          <Star className="w-3 h-3 fill-brand-orange text-brand-orange" />
          {SLIDES[idx].tag}
        </motion.div>

        {/* Headline */}
        <AnimatePresence mode="wait">
          <motion.h2
            key={`h-${idx}`}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.65, delay: 0.08 }}
            className="font-serif text-5xl sm:text-6xl md:text-7xl leading-tight mb-4"
            aria-hidden="true"
          >
            <span className="block text-white/90 font-light italic">
              {SLIDES[idx].title}
            </span>
            <span className="block font-extrabold bg-orange-shimmer bg-[length:200%_auto] bg-clip-text text-transparent animate-shimmer">
              {SLIDES[idx].highlight}
            </span>
          </motion.h2>
        </AnimatePresence>

        <motion.p
          key={`sub-${idx}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.18 }}
          className="text-[#F9B233] text-base md:text-xl font-dark mb-10 tracking-wide leading-relaxed"
        >
          {SLIDES[idx].sub}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap gap-4 justify-center mb-12"
        >
          <button
            onClick={() => scrollTo("#menu")}
            className="btn-orange !text-sm"
          >
            🍽 Explore Menu
          </button>
          <button
            onClick={() => scrollTo("#location")}
            className="btn-outline-white"
          >
            <MapPin className="w-4 h-4" /> Find Us
          </button>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="flex flex-wrap gap-5 justify-center"
        >
          <a
            href="https://www.google.com/maps/search/?api=1&query=Himalayan+Bowl+%26+Juice+Bar+Gainesville+FL"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-white/65 text-sm font-semibold hover:text-brand-orange transition-colors duration-200 cursor-pointer"
          >
            <Star className="w-4 h-4 fill-brand-orange text-brand-orange" /> 4.9
          </a>
          {[
            {
              icon: <MapPin className="w-4 h-4 text-brand-orange" />,
              text: "Gainesville, FL",
            },
            {
              icon: <Clock className="w-4 h-4 text-brand-orange" />,
              text: "Open Daily 11 AM",
            },
          ].map((b) => (
            <div
              key={b.text}
              className="flex items-center gap-1.5 text-white/65 text-sm font-semibold"
            >
              {b.icon} {b.text}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-[88px] left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === idx ? "w-7 bg-brand-orange" : "w-1.5 bg-white/35"
            }`}
          />
        ))}
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 opacity-50">
        <div className="w-px h-10 bg-gradient-to-b from-transparent to-brand-orange animate-scroll-line" />
        <ChevronDown className="w-4 h-4 text-brand-orange animate-bounce" />
      </div>
    </section>
  );
}
